import { useRef, useCallback } from "react";
import useAxios from "./useAxios";
import { getGuestTokenConfig } from "../constants";

// SubMatch is a fully separate resource on the live API - not nested under
// the parent Match response - so a team match's own sub-matches always need
// their own fetch, filtered by parent_match (see backend Swagger:
// GET /api/v1.0/sub-matches/?parent_match=<id>).
export async function fetchSubMatches(api, tournamentId, parentMatchId) {
    if (!parentMatchId) return [];
    const config = getGuestTokenConfig(tournamentId);
    const res = await api.get(`/api/v1.0/sub-matches/?parent_match=${parentMatchId}`, config);
    return res.data?.results || [];
}

export async function createSubMatch(api, tournamentId, parentMatchId, { order = 1, is_doubles_match = false } = {}) {
    const config = getGuestTokenConfig(tournamentId, { 'Content-Type': 'application/json' });
    const res = await api.post(`/api/v1.0/sub-matches/`, { parent_match: parentMatchId, order, is_doubles_match }, config);
    return res.data;
}

export async function deleteSubMatch(api, tournamentId, subMatchId) {
    const config = getGuestTokenConfig(tournamentId);
    await api.delete(`/api/v1.0/sub-matches/${subMatchId}/`, config);
}

// Same debounced per-id queue/flush shape as useMatchSync.js's syncMatch,
// pointed at the separate /sub-matches/{id}/ resource instead of the parent
// tournament-match endpoint - keyed by the sub-match's own id.
export const useSubMatchSync = (tournamentId, onApiSuccess) => {
    const api = useAxios();
    const isRequesting = useRef({});
    const pendingPayloads = useRef({});

    const syncSubMatch = useCallback((subMatchId, newFields) => {
        if (!pendingPayloads.current[subMatchId]) {
            pendingPayloads.current[subMatchId] = { main: {} };
        }
        Object.entries(newFields).forEach(([key, value]) => {
            if (key === 'sets') {
                pendingPayloads.current[subMatchId].sets = value;
            } else {
                pendingPayloads.current[subMatchId].main[key] = value;
            }
        });

        if (isRequesting.current[subMatchId]) return;

        const flushQueue = async () => {
            if (!pendingPayloads.current[subMatchId]) return;
            const accumulated = pendingPayloads.current[subMatchId];
            const payload = { ...accumulated.main };

            if (accumulated.sets) {
                // Same quirk useMatchSync.js already works around on the
                // parent-match endpoint: sending a set's own "id" back reads
                // as creating a new set and fails with "set with this id
                // already exists" - the backend upserts by (parent, number)
                // instead, and omitting "number" on the first set defaults
                // it to 1 there.
                payload.sets = accumulated.sets.map(({ id, number, ...rest }, idx) => {
                    const setNumber = number !== undefined ? number : idx + 1;
                    return setNumber > 1 ? { ...rest, number: setNumber } : rest;
                });
            }

            if (Object.keys(payload).length === 0) return;

            delete pendingPayloads.current[subMatchId];
            isRequesting.current[subMatchId] = true;

            const config = getGuestTokenConfig(tournamentId, { 'Content-Type': 'application/json' });

            try {
                const res = await api.patch(`/api/v1.0/sub-matches/${subMatchId}/`, payload, config);
                if (onApiSuccess) onApiSuccess(res.data);
            } catch (e) {
                console.error("Sub-match sync error:", e);
            } finally {
                isRequesting.current[subMatchId] = false;
                if (pendingPayloads.current[subMatchId]) {
                    flushQueue();
                }
            }
        };

        flushQueue();
    }, [api, tournamentId, onApiSuccess]);

    return syncSubMatch;
};
